import React, { useEffect, useState, useCallback } from "react";
import { useProfile } from "../Components/Hooks/UserHooks";
import { getrolewisesidemenu } from "../pages/Roles/_requests-roleaccess";

const useNavData = () => {
  const { userProfile } = useProfile();
  const currentRole = userProfile?.roleId;
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [dynamicStates, setDynamicStates] = useState<{ [key: string]: boolean }>({});
  const [groupedItems, setGroupedItems] = useState<{ [key: string]: any[] }>({});
  const [statesInitialized, setStatesInitialized] = useState(false);

  // Handle menu expand/collapse click
  const handleMenuClick = useCallback((label: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setDynamicStates((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
    
    // Reset sidebar active classes for two-column menu
    if (e?.currentTarget?.getAttribute("sub-items")) {
      const menu = document.getElementById("two-column-menu");
      const activeIcons = menu?.querySelectorAll(".nav-icon.active") || [];
      activeIcons.forEach((icon) => {
        icon.classList.remove("active");
        const id = icon.getAttribute("sub-items");
        const subMenu = document.getElementById(id as string);
        subMenu?.classList.remove("show");
      });
    }
  }, []);

  // Fetch and group side menu items based on role
  const fetchSideMenu = useCallback(async (roleId: any) => {
    try {
      const response = await getrolewisesidemenu(roleId);
      // Handle both full response object and flattened data from interceptor
      const items = Array.isArray(response) ? response : (response?.data && Array.isArray(response.data) ? response.data : []);
      
      const grouped: { [key: string]: any[] } = {};
      const initialStates: { [key: string]: boolean } = {};

      items.forEach((item: any) => {
        const title = item.module.title?.name || "Other";
        if (!grouped[title]) {
          grouped[title] = [];
        }

        const groupedItem = {
          id: item.module.titleId,
          label: item.module.name,
          icon: item.module.icon,
          titleIcon: item.module.title?.icon,
          link: `/${item.module.link}`,
          parentId: title,
          listOrder: item.module.listOrder || 999,
          titleListOrder: item.module.title?.listOrder || 999,
        };

        grouped[title].push(groupedItem);
      });

      const sortedGroupKeys = Object.keys(grouped).sort((a, b) => {
        const aOrder = grouped[a][0]?.titleListOrder || 999;
        const bOrder = grouped[b][0]?.titleListOrder || 999;
        return aOrder - bOrder;
      });

      sortedGroupKeys.forEach((key) => {
        grouped[key].sort((a, b) => a.listOrder - b.listOrder);
        initialStates[key] = false;
      });

      const sortedGroupedItems: { [key: string]: any[] } = {};
      sortedGroupKeys.forEach((key) => {
        sortedGroupedItems[key] = grouped[key];
      });

      setGroupedItems(sortedGroupedItems);
      setDynamicStates(initialStates);
      setStatesInitialized(true);
    } catch (error) {
      console.error("Error fetching side menu:", error);
    }
  }, []);

  // Fetch side menu when role changes
  useEffect(() => {
    if (currentRole) {
      fetchSideMenu(currentRole);
    }
  }, [currentRole, fetchSideMenu]);

  // Build menu items after states are initialized
  useEffect(() => {
    if (statesInitialized) {
      const builtMenuItems = Object.keys(groupedItems).map((key) => {
        const firstItem = groupedItems[key][0];
        const icon = firstItem?.titleIcon || "mdi mdi-folder";

        return {
          id: key,
          label: key,
          icon,
          link: "/#",
          stateVariables: dynamicStates[key],
          click: handleMenuClick(key),
          subItems: groupedItems[key],
        };
      });

      setMenuItems(builtMenuItems);
    }
  }, [statesInitialized, dynamicStates, groupedItems, handleMenuClick]);

  return { menuItems, loading: !statesInitialized && !!currentRole };
};

export default useNavData;
